import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, takeUntil, skip, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MastersService } from '../../services/masters/masters.service';
import { DynamicListingComponent, ColumnConfig, ActionConfig, ActionEvent } from '../../dynamic-listing/dynamic-listing';
import { DynamicForm } from '../../dynamic-form/dynamic-form';
import { SVG_ICONS } from '../../utils/svg-icons.utils';
import { MasterConfig, MASTER_CONFIGS } from '../master-config';
import { ToasterService } from '@ui-nuvertos/auth';

@Component({
  selector: 'lib-generic-master',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DynamicListingComponent,
    DynamicForm
  ],
  templateUrl: './generic-master.html',
  styleUrls: ['./generic-master.css'],
})
export class GenericMaster implements OnInit, OnDestroy {
  config!: MasterConfig;
  isLoading = false;
  tableData: any[] = [];
  private destroy$ = new Subject<void>();

  // Pagination
  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;

  // View mode
  viewMode: 'list' | 'form' = 'list';
  isEditMode = false;
  selectedItemId: string | number | null = null;
  showInactives = false;
  formData: any = null;
  formGroup: FormGroup;

  // Dropdown dependency options (keyed by fieldKey)
  dropdownOptionsMap: Record<string, any[]> = {};

  columnConfigs: ColumnConfig[] = [];

  actionConfigs: ActionConfig[] = [
    {
      key: 'toggle',
      label: 'Status',
      tooltip: 'Toggle Active/Inactive',
      type: 'toggle'
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE
    }
  ];

  headerActionConfigs: ActionConfig[] = [
    { key: 'create', label: 'Add', tooltip: 'Add New', severity: 'primary' }
  ];

  private readonly BACKEND_MANAGED_FIELDS = new Set([
    'id', 'created_at', 'created_by', 'updated_at', 'updated_by',
    'is_deleted', 'deleted_at', 'deleted_by'
  ]);

  formFields: any[] = [];

  // Cell templates for custom column rendering
  @ViewChild('colorCodeCell', { static: true }) colorCodeCell!: TemplateRef<any>;
  cellTemplates: Record<string, TemplateRef<any>> = {};

  // Read-only fields that should not be sent in create/update payloads
  private readonly READ_ONLY_FIELDS = new Set([
    'id', 'created_at', 'updated_at', 'created_by', 'updated_by',
    'is_deleted', 'deleted_at', 'deleted_by'
  ]);

  constructor(
    private mastersService: MastersService,
    private toasterService: ToasterService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.formGroup = this.fb.group({});
  }

  private resolveConfig(): boolean {
    const url = this.router.url;
    // Match /settings/masters/{slug} or /masters/{slug}
    const match = url.match(/\/(?:settings\/)?masters\/([^\/\?#]+)/);
    const routeSlug = match ? match[1] : '';

    // Direct lookup first
    let config = MASTER_CONFIGS[routeSlug];

    // If not found, try module-prefixed lookup (e.g. 'departments' → 'bmw-departments')
    if (!config) {
      const prefixedKey = Object.keys(MASTER_CONFIGS).find(
        key => MASTER_CONFIGS[key].module && MASTER_CONFIGS[key].routeSlug.endsWith(routeSlug)
      );
      if (prefixedKey) {
        config = MASTER_CONFIGS[prefixedKey];
      }
    }

    if (!config) {
      console.error(`No master config found for route slug: ${routeSlug}`);
      return false;
    }
    this.config = config;
    return true;
  }

  async loadMasters(page: number = 1) {
    try {
      this.isLoading = true;

      // Load dropdown deps for display mapping
      if (this.config.dropdownDeps) {
        for (const dep of this.config.dropdownDeps) {
          if (!this.dropdownOptionsMap[dep.fieldKey]?.length) {
            await this.loadDropdownDep(dep);
          }
        }
      }

      const params: any = {
        limit: this.rowsPerPage,
        offset: (page - 1) * this.rowsPerPage
      };
      if (this.showInactives) {
        params.include_inactive = 'true';
      }

      const response: any = await lastValueFrom(
        this.mastersService.getAll(this.config.apiSlug, params, this.config?.module)
      );

      let data = response?.data?.[this.config.dataKey] || [];

      // Map dropdown dep IDs to display names for listing
      if (this.config.dropdownDeps) {
        data = data.map((item: any) => {
          const mapped = { ...item };
          for (const dep of this.config.dropdownDeps!) {
            const options = this.dropdownOptionsMap[dep.fieldKey] || [];
            const fieldVal = String(item[dep.fieldKey] || '');
            const match = options.find(
              (opt: any) => String(opt.id) === fieldVal || String(opt.name) === fieldVal
            );
            mapped[dep.displayKey] = match?.name || fieldVal;
          }
          return mapped;
        });
      }

      this.tableData = data;
      this.totalCount = response?.data?.total || this.tableData.length;
      this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
      this.pageIndex = page;

      if (response?.data?.references && !this.columnConfigs.length) {
        this.updateColumnsFromReferences(response.data.references);
      }

      // If columns still empty (list endpoint didn't include references), load separately
      if (!this.columnConfigs.length) {
        try {
          const refResponse: any = await lastValueFrom(
            this.mastersService.getReferences(this.config.apiSlug, this.config?.module)
          );
          if (refResponse?.data?.references) {
            this.updateColumnsFromReferences(refResponse.data.references);
          }
        } catch (refError) {
          console.error('Error loading references:', refError);
        }
      }
    } catch (error) {
      console.error('Error loading masters:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadDropdownDep(dep: { fieldKey: string; loadApiSlug: string; loadDataKey: string }) {
    try {
      const response: any = await lastValueFrom(
        this.mastersService.getAll(dep.loadApiSlug, {}, this.config?.module)
      );
      const items = response?.data?.[dep.loadDataKey] || response?.data || [];
      this.dropdownOptionsMap[dep.fieldKey] = Array.isArray(items)
        ? items
            .filter((item: any) => item.is_active !== false && item.is_deleted !== true)
            .map((item: any) => {
              const name = item.name || item.type_name || '';
              const id = item.id ?? name;
              return { id, name, value: id, label: name };
            })
        : [];
    } catch (error) {
      console.error(`Error loading dropdown dep ${dep.fieldKey}:`, error);
      this.dropdownOptionsMap[dep.fieldKey] = [];
    }
  }

  updateColumnsFromReferences(references: any[]) {
    // Set up cell templates for color_code columns
    const hasColorCode = references.some(ref => ref.key === 'color_code');
    if (hasColorCode && this.colorCodeCell) {
      this.cellTemplates = { color_code: this.colorCodeCell };
    }

    this.columnConfigs = references.map(ref => {
      let columnType: 'text' | 'date' | 'currency' | 'number' | 'status' | 'custom' = 'text';
      if (ref.type === 'datetime' || ref.type === 'date') {
        columnType = 'date';
      } else if (ref.type === 'number' || ref.type === 'double') {
        columnType = 'number';
      } else if (ref.type === 'boolean') {
        columnType = 'status';
      }

      // For dropdown dep fields, show display name column instead
      if (this.config.dropdownDeps) {
        const dep = this.config.dropdownDeps.find(d => d.fieldKey === ref.key);
        if (dep) {
          return {
            key: dep.displayKey,
            header: dep.displayLabel,
            type: 'text' as const,
            customField: false
          };
        }
      }

      return {
        key: ref.key,
        header: ref.label,
        type: columnType,
        customField: false
      };
    });
  }

  async loadItemForEdit(id: string | number) {
    try {
      this.isLoading = true;
      await this.loadFormReferences();

      const response: any = await lastValueFrom(
        this.mastersService.getById(this.config.apiSlug, id, this.config?.module)
      );

      // Try singular data key first, then fall back to response.data
      const singular = this.config.singularDataKey;
      this.formData = (singular && response?.data?.[singular])
        || response?.data
        || (singular && response?.[singular])
        || response;

      this.initializeForm();
    } catch (error) {
      console.error('Error loading item for edit:', error);
      alert('Failed to load item for editing');
    } finally {
      this.isLoading = false;
    }
  }

  async loadFormReferences() {
    try {
      // Load dropdown dependencies
      if (this.config.dropdownDeps) {
        for (const dep of this.config.dropdownDeps) {
          await this.loadDropdownDep(dep);
        }
      }

      const response: any = await lastValueFrom(
        this.mastersService.getReferences(this.config.apiSlug, this.config?.module)
      );

      if (response?.data?.references) {
        this.formFields = response.data.references.map((ref: any, index: number) => {
          let fieldType = 'input';
          let options: any[] = [];
          let displayName = ref.label || ref.display_name || '';

          // Check if this field is a dropdown dep
          const dep = this.config.dropdownDeps?.find(d => d.fieldKey === ref.key);
          if (dep) {
            fieldType = 'select';
            options = this.dropdownOptionsMap[dep.fieldKey] || [];
            displayName = dep.displayLabel;
          } else if (ref.type === 'datetime' || ref.type === 'date') {
            fieldType = 'date';
          } else if (ref.type === 'double') {
            fieldType = 'double';
          } else if (ref.type === 'number') {
            fieldType = 'number';
          } else if (ref.type === 'boolean') {
            fieldType = 'checkbox';
          } else if (ref.type === 'string' && ref.key === 'color_code') {
            fieldType = 'color';
          } else if (ref.type === 'string' && ref.key.includes('description')) {
            fieldType = 'textarea';
          } else if (ref.type === 'label') {
            fieldType = 'label';
          }

          // Detect fields that should only allow numeric input
          const numericOnly = ref.key === 'account_number' || ref.key.endsWith('_account_number');

          return {
            field_name: ref.key || ref.field_name || '',
            display_name: displayName,
            field_type: fieldType,
            data_type: ref.type || ref.data_type || 'string',
            is_mandatory: ref.required || ref.is_mandatory || false,
            is_visible: ref.is_visible !== undefined ? ref.is_visible : true,
            is_custom_field: ref.is_custom_field || false,
            tab_id: ref.tab_id || 1,
            sub_tab_id: ref.sub_tab_id || 0,
            display_order: ref.display_order !== undefined ? ref.display_order : index + 1,
            validation_rules: ref.validation_rules || '',
            help_text: ref.help_text || '',
            default_value: ref.default_value || '',
            is_active: ref.is_active !== undefined ? ref.is_active : true,
            options: options,
            numeric_only: numericOnly
          };
        });
      }
    } catch (error) {
      console.error('Error loading form references:', error);
    }
  }

  initializeForm() {
    const formControls: any = {};

    this.formFields.forEach(field => {
      const key = field.field_name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      let value = this.formData?.[field.field_name]
        || this.formData?.[key]
        || this.formData?.[field.field_name.toLowerCase()]
        || this.formData?.[field.field_name.toLowerCase().replace(/[^a-z0-9]/g, '_')]
        || (field.field_type === 'checkbox' ? false : '');

      const validators = field.is_mandatory ? [Validators.required] : [];
      formControls[key] = [value, validators];
    });

    if (this.formData) {
      Object.keys(this.formData).forEach(dataKey => {
        const normalizedKey = dataKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
        if (!formControls[normalizedKey]) {
          formControls[normalizedKey] = [this.formData[dataKey], []];
        }
      });
    }

    this.formGroup = this.fb.group(formControls);
  }

  onActionClicked(event: ActionEvent) {
    const { action, row } = event;

    switch (action) {
      case 'create':
        this.onCreateClick();
        break;
      case 'edit':
        this.editItem(row);
        break;
      case 'toggle':
        this.toggleStatus(row);
        break;
      case 'delete':
        this.deleteItem(row);
        break;
    }
  }

  private get mastersBasePath(): string {
    // Detect base path from current URL
    const url = this.router.url;
    const match = url.match(/(.*\/masters)\//);
    return match ? match[1] : '/settings/masters';
  }

  editItem(row: any) {
    const slug = this.config?.module
      ? this.config.routeSlug.replace(`${this.config.module}-`, '')
      : this.config.routeSlug;
    this.router.navigate([`${this.mastersBasePath}/${slug}/edit`, row.id]);
  }

  private getDisplayName(row: any): string {
    if (this.config.displayNameField && row[this.config.displayNameField]) {
      return row[this.config.displayNameField];
    }
    if (row.name) return row.name;
    const nameField = Object.keys(row).find(k => k.endsWith('_name') && row[k] && k !== 'table_name');
    if (nameField) return row[nameField];
    return row.label || row.title || String(row.id);
  }

  async toggleStatus(row: any) {
    // ngModel already flipped is_active, so row.is_active is the NEW desired value
    const newStatus = Boolean(row.is_active);
    const statusText = newStatus ? 'activate' : 'deactivate';
    const displayName = this.getDisplayName(row);

    if (confirm('Are you sure you want to ' + statusText + ' "' + displayName + '"?')) {
      try {
        this.isLoading = true;
        const currentItem: any = await lastValueFrom(
          this.mastersService.getById(this.config.apiSlug, row.id, this.config?.module)
        );

        const itemData = currentItem?.data?.[this.config.singularDataKey!] || currentItem?.data || currentItem;
        const updateData = {
          ...itemData,
          is_active: newStatus
        };

        // Strip read-only fields
        this.READ_ONLY_FIELDS.forEach(f => delete updateData[f]);

        await lastValueFrom(
          this.mastersService.update(this.config.apiSlug, row.id, updateData, this.config?.module)
        );

        this.toasterService.success('Success', `Record ${statusText}d successfully`);
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error toggling status:', error);
        this.toasterService.error('Error', 'Failed to toggle status');
        // Revert the toggle on error — use setTimeout to ensure change detection picks it up
        setTimeout(() => {
          row.is_active = !newStatus;
          this.cdr.detectChanges();
        });
      } finally {
        this.isLoading = false;
      }
    } else {
      // User cancelled — revert the toggle using setTimeout to ensure ToggleSwitch re-renders
      setTimeout(() => {
        row.is_active = !newStatus;
        this.cdr.detectChanges();
      });
    }
  }

  async deleteItem(row: any) {
    const displayName = this.getDisplayName(row);
    if (confirm('Are you sure you want to delete "' + displayName + '"?')) {
      try {
        await lastValueFrom(
          this.mastersService.remove(this.config.apiSlug, row.id, this.config?.module)
        );
        this.toasterService.success('Success', 'Record deleted successfully');
        this.loadMasters(this.pageIndex);
      } catch (error: any) {
        console.error('Error deleting item:', error);
        const errorMessage = error?.error?.message || 'Failed to delete item';
        this.toasterService.error('Error', errorMessage);
      }
    }
  }

  onPageChanged(page: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page, limit: this.rowsPerPage },
      queryParamsHandling: 'merge'
    });
  }

  onShowInactivesChange() {
    this.pageIndex = 1;
    this.loadMasters(this.pageIndex);
  }

  onCreateClick() {
    const slug = this.config?.module
      ? this.config.routeSlug.replace(`${this.config.module}-`, '')
      : this.config.routeSlug;
    this.router.navigate([`${this.mastersBasePath}/${slug}/create`]);
  }

  async ngOnInit() {
    if (!this.resolveConfig()) return;

    await this.checkRouteAndLoad();

    let previousUrl = this.router.url;

    this.route.params
      .pipe(
        skip(1),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        await this.checkRouteAndLoad();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event: any) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        if (currentUrl !== previousUrl) {
          previousUrl = currentUrl;
          await this.checkRouteAndLoad();
        }
      });

    this.route.queryParams
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(params => {
        if (this.viewMode === 'list' && params['page']) {
          const page = parseInt(params['page']) || 1;
          this.loadMasters(page);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async checkRouteAndLoad() {
    const url = this.router.url;
    let routeParams = this.route.snapshot.params;
    let childRoute = this.route.snapshot.firstChild;
    while (childRoute) {
      routeParams = { ...routeParams, ...childRoute.params };
      childRoute = childRoute.firstChild;
    }

    let id = routeParams['id'];
    if (!id && url.includes('/edit/')) {
      const match = url.match(/\/edit\/([^\/]+)/);
      if (match) id = match[1];
    }
    if (!id && url.includes('/view/')) {
      const match = url.match(/\/view\/([^\/]+)/);
      if (match) id = match[1];
    }

    if (url.includes('/create') || routeParams['action'] === 'create') {
      this.viewMode = 'form';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.formData = {};
      this.isLoading = true;
      await this.loadFormReferences();
      this.initializeForm();
      this.isLoading = false;
    } else if (id && (url.includes('/edit') || url.includes('/view'))) {
      this.selectedItemId = id;
      this.isEditMode = url.includes('/edit');
      this.viewMode = 'form';
      await this.loadItemForEdit(id);
    } else {
      this.viewMode = 'list';
      this.isEditMode = false;
      this.selectedItemId = null;

      // Hide internal Add button if header already provides redirect buttons
      const routeActions = this.route.snapshot.data?.['actions'];
      if (routeActions?.redirectButtons?.length > 0) {
        this.headerActionConfigs = [];
      }

      const page = parseInt(this.route.snapshot.queryParams['page']) || 1;
      this.loadMasters(page);
    }
  }

  onFormFieldChanged(event: any) {
    if (!this.formData) {
      this.formData = {};
    }
    this.formData[event.key] = event.value;
  }

  async onFormSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.toasterService.error('Validation Error', 'Please fill in all required fields');
      return;
    }

    try {
      this.isLoading = true;
      const rawValue = this.formGroup.value;
      const formValue: any = {};
      for (const key of Object.keys(rawValue)) {
        if (!this.BACKEND_MANAGED_FIELDS.has(key)) {
          formValue[key] = rawValue[key];
        }
      }

      // Strip read-only fields that should not be sent to the API
      this.READ_ONLY_FIELDS.forEach(f => delete formValue[f]);

      if (this.isEditMode && this.selectedItemId) {
        await lastValueFrom(
          this.mastersService.update(this.config.apiSlug, this.selectedItemId, formValue, this.config?.module)
        );
        this.toasterService.success('Success', 'Record updated successfully');
      } else {
        await lastValueFrom(
          this.mastersService.create(this.config.apiSlug, formValue, this.config?.module)
        );
        this.toasterService.success('Success', 'Record created successfully');
      }
      const slug = this.config?.module
        ? this.config.routeSlug.replace(`${this.config.module}-`, '')
        : this.config.routeSlug;
      this.router.navigate([`${this.mastersBasePath}/${slug}`]);
    } catch (error: any) {
      console.error('Error saving item:', error);
      const errorMessage = error?.error?.message || 'Failed to save item';
      this.toasterService.error('Error', errorMessage);
    } finally {
      this.isLoading = false;
    }
  }

  onCancel() {
    const slug = this.config?.module
      ? this.config.routeSlug.replace(`${this.config.module}-`, '')
      : this.config.routeSlug;
    this.router.navigate([`${this.mastersBasePath}/${slug}`]);
  }
}
